import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  listFrameworks,
  deleteFramework,
  FrameworkSummary,
} from "../services/frameworkAdminService";
import ConfirmModal from "./admin/ConfirmModal";

interface FrameworksTabProps {
  getToken: () => string | undefined;
}

const FrameworksTab: React.FC<FrameworksTabProps> = ({ getToken }) => {
  const navigate = useNavigate();
  const { orgSlug } = useParams();
  const [frameworks, setFrameworks] = useState<FrameworkSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FrameworkSummary | null>(
    null,
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setFrameworks(await listFrameworks(getToken()));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load frameworks");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    void load();
  }, [load]);

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await deleteFramework(pendingDelete.id, getToken());
      setPendingDelete(null);
      await load();
    } catch (e) {
      // A 409 lands here when rubrics still reference the framework. The row
      // stays put and the server's message — which names the count — is what
      // the user sees.
      setError(e instanceof Error ? e.message : "Delete failed");
      setPendingDelete(null);
    }
  };

  return (
    <div className="text-on-surface">
      <div className="flex items-center justify-end mb-4">
        <button
          type="button"
          className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold"
          onClick={() => navigate(`/${orgSlug}/frameworks/new`)}
        >
          + New Framework
        </button>
      </div>

      {error && <div className="mb-3 text-error text-sm">{error}</div>}
      {loading ? (
        <div className="text-slate-400 text-sm">Loading…</div>
      ) : frameworks.length === 0 ? (
        <div className="text-slate-400 text-sm">
          No frameworks yet. Create one.
        </div>
      ) : (
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="text-left text-slate-400 border-b border-outline-variant/30">
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">Description</th>
              <th className="py-2 pr-4">Rubrics</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {frameworks.map((f) => (
              <tr
                key={f.id}
                className="border-b border-outline-variant/20 hover:bg-surface-container"
              >
                <td className="py-2 pr-4 font-semibold">{f.name}</td>
                <td className="py-2 pr-4 text-slate-400">{f.description}</td>
                <td className="py-2 pr-4">{f.rubricCount}</td>
                <td className="py-2 text-right whitespace-nowrap">
                  {f.canEdit ? (
                    <>
                      <button
                        type="button"
                        className="text-primary hover:underline mr-3"
                        onClick={() =>
                          navigate(`/${orgSlug}/frameworks/${f.id}/edit`)
                        }
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-error hover:underline"
                        onClick={() => setPendingDelete(f)}
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <span className="text-slate-500 text-xs">Global</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pendingDelete && (
        <ConfirmModal
          title="Delete framework"
          body={`Delete "${pendingDelete.name}"? This cannot be undone.`}
          icon="delete"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          destructive
          onConfirm={confirmDelete}
          onClose={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
};

export default FrameworksTab;
